import React, { FC, useCallback, useMemo, useState } from 'react'
import { Button, Heading, Icon, Modal, Text, Checkbox, Input, Money } from '@afterpaytouch/core'
import { useConsumerSizes } from '../../../../../utils/responsive'
import { Section75DisputeModalSteps, SelectedItem, useSection75DisputeModalContext } from '../../Section75DisputeModalContext'
import { isAmountOverSection75Threshold, checkAmount, convertSection75ItemToConsumerCurrency, section75ItemQuantity, section75LineTotal } from '../../../utils'
import { useSection75Eligibility } from '../../hooks'
import { useTranslation } from 'next-i18next'

interface ManualItem {
  id: number
  name: string
  amount: string
}
const I18N_NAMESPACE = ['common', 'dispute']

export const ChooseItems: FC = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const { textSize } = useConsumerSizes()
  const { setStep, order, selectedItems, setSelectedItems, setCurrentItemIndex } = useSection75DisputeModalContext()
  const items = order?.orderDetail?.items || []

  const [manualItems, setManualItems] = useState<ManualItem[]>([{ id: 1, name: '', amount: '' }])

  const normalizedItems = useMemo(() => {
    const consumerOrderTotal = order?.orderSummary?.orderTotal
    if (consumerOrderTotal === undefined) {
      return []
    }

    const convertedItems: typeof items = []
    for (const item of items) {
      if (!item.name || item.price === undefined) {
        return []
      }

      const convertedPrice = convertSection75ItemToConsumerCurrency(item.price, consumerOrderTotal, order.exchangeRate)
      if (convertedPrice === null) {
        return []
      }

      convertedItems.push({
        ...item,
        price: convertedPrice,
      })
    }

    return convertedItems
  }, [items, order?.exchangeRate, order?.orderSummary?.orderTotal])

  const shouldShowManualEntry = items.length === 0 || normalizedItems.length !== items.length

  const backHandler = useCallback(() => {
    setStep(Section75DisputeModalSteps.ClaimEligibility)
  }, [setStep])

  const hasSection75Eligibility = useSection75Eligibility(selectedItems)

  const hasManualItemsEligibility = useMemo(() => {
    return manualItems.some((item) => {
      if (!item.name.trim() || !item.amount.trim()) {
        return false
      }
      const numericAmount = parseFloat(item.amount)
      return checkAmount(numericAmount) && isAmountOverSection75Threshold(item.amount)
    })
  }, [manualItems])

  const handleNext = useCallback(() => {
    if (shouldShowManualEntry) {
      const validManualItems = manualItems.filter((item) => item.name.trim() && item.amount.trim())
      if (validManualItems.length > 0) {
        const newSelectedItems: SelectedItem[] = validManualItems.map((item, index) => ({
          key: index,
          name: item.name.trim(),
          amount: {
            amount: item.amount.trim(),
            currency: order?.orderSummary?.orderTotal?.currency || 'GBP',
          },
          itemAmount: {
            amount: item.amount.trim(),
            currency: order?.orderSummary?.orderTotal?.currency || 'GBP',
          },
          quantity: 1,
          reason: '',
          description: '',
          fileKeys: [],
        }))
        setSelectedItems(newSelectedItems)

        if (hasManualItemsEligibility) {
          setCurrentItemIndex(0)
          setStep(Section75DisputeModalSteps.DisputeDetails)
        } else {
          setStep(Section75DisputeModalSteps.NotEligible)
        }
      }
    } else if (selectedItems.length > 0) {
      if (hasSection75Eligibility) {
        setCurrentItemIndex(0)
        setStep(Section75DisputeModalSteps.DisputeDetails)
      } else {
        setStep(Section75DisputeModalSteps.NotEligible)
      }
    }
  }, [
    setStep,
    selectedItems.length,
    setCurrentItemIndex,
    shouldShowManualEntry,
    manualItems,
    order,
    setSelectedItems,
    hasManualItemsEligibility,
    hasSection75Eligibility,
  ])

  const handleItemToggle = useCallback(
    (itemKey: number, itemName: string, checked: boolean) => {
      if (checked) {
        const orderItem = normalizedItems[itemKey]
        const itemPrice = orderItem?.price?.amount ?? '0'
        const itemCurrency = orderItem?.price?.currency ?? 'GBP'
        const itemQuantity = section75ItemQuantity(orderItem?.quantity)

        const newSelectedItem: SelectedItem = {
          key: itemKey,
          name: itemName,
          amount: {
            amount: section75LineTotal(itemPrice, itemQuantity),
            currency: itemCurrency,
          },
          itemAmount: {
            amount: itemPrice,
            currency: itemCurrency,
          },
          quantity: itemQuantity,
          reason: '',
          description: '',
          fileKeys: [],
        }
        setSelectedItems([...selectedItems, newSelectedItem])
      } else {
        setSelectedItems(selectedItems.filter((item) => item.key !== itemKey))
      }
    },
    [selectedItems, setSelectedItems, normalizedItems]
  )

  const handleManualItemNameChange = useCallback((itemId: number, name: string) => {
    setManualItems((prev) => prev.map((item) => (item.id === itemId ? { ...item, name } : item)))
  }, [])

  const handleManualItemAmountChange = useCallback((itemId: number, amount: string) => {
    setManualItems((prev) => prev.map((item) => (item.id === itemId ? { ...item, amount } : item)))
  }, [])

  const handleAddAnotherItem = useCallback(() => {
    setManualItems((prev) => {
      const newId = Math.max(...prev.map((item) => item.id), 0) + 1
      return [...prev, { id: newId, name: '', amount: '' }]
    })
  }, [])

  const handleRemoveManualItem = useCallback((itemId: number) => {
    setManualItems((prev) => prev.filter((item) => item.id !== itemId))
  }, [])

  const isNextDisabled = useMemo(() => {
    if (shouldShowManualEntry) {
      return !manualItems.some((item) => {
        if (!item.name.trim() || !item.amount.trim()) {
          return false
        }
        const numericAmount = parseFloat(item.amount)
        return checkAmount(numericAmount)
      })
    }
    return selectedItems.length === 0
  }, [selectedItems, shouldShowManualEntry, manualItems])

  return (
    <>
      <Modal.Header divider={true}>
        <div className='absolute z-10 pt-1'>
          <Button.Link size='Small' onClick={backHandler} aria-label={t('common:back')}>
            <Icon.CaretLeft size='Small' />
          </Button.Link>
        </div>
        <div className='text-center'>
          <Heading size='L'>{t('dispute:section75DisputeModal:chooseItems:heading')}</Heading>
        </div>
      </Modal.Header>
      <Modal.Content>
        {shouldShowManualEntry ? (
          <>
            {manualItems.map((item, index) => (
              <div key={item.id} className='mb-6'>
                <div className='mb-4'>
                  <div className='mb-2 flex items-center justify-between'>
                    <Text size={textSize}>{t('dispute:section75DisputeModal:chooseItems:descriptionLabel')}</Text>
                    {manualItems.length > 1 && (
                      <Button.Link onClick={() => handleRemoveManualItem(item.id)} color='Fire'>
                        {t('dispute:section75DisputeModal:chooseItems:removeItem')}
                      </Button.Link>
                    )}
                  </div>
                  <Input.Text
                    label={t('dispute:section75DisputeModal:chooseItems:descriptionLabel')}
                    value={item.name}
                    onChange={(e) => handleManualItemNameChange(item.id, e.target.value)}
                    fullWidth
                  />
                </div>
                <div className='mb-4'>
                  <Text size={textSize} className='mb-2'>
                    {t('dispute:section75DisputeModal:chooseItems:amountLabel')}
                  </Text>
                  <Input.Number
                    label={t('dispute:section75DisputeModal:chooseItems:amountLabel')}
                    value={item.amount}
                    onChange={(e) => handleManualItemAmountChange(item.id, e.target.value)}
                    fullWidth
                  />
                </div>
              </div>
            ))}

            <div className='mb-6'>
              <Button.Link onClick={handleAddAnotherItem}>{t('dispute:section75DisputeModal:chooseItems:addItem')}</Button.Link>
            </div>

            <div className='mt-auto'>
              <Button padding='Fluid' size='Large' onClick={handleNext} disabled={isNextDisabled}>
                {t('dispute:section75DisputeModal:chooseItems:next')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div>
              <Text size={textSize}>{t('dispute:section75DisputeModal:chooseItems:selectItems')}</Text>
            </div>

            <div className='mt-6 space-y-3'>
              {normalizedItems.map((item, index) => {
                const itemKey = index
                const itemPrice = item.price?.amount ?? '0'
                const itemQuantity = section75ItemQuantity(item.quantity)

                return (
                  <div
                    key={`item-${itemKey}`}
                    className='flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:bg-gray-50'
                  >
                    <div className='flex flex-1 items-center'>
                      <Checkbox
                        id={`item-${itemKey}`}
                        name={`item-${itemKey}`}
                        onChange={(event) => handleItemToggle(itemKey, item.name, event.target.checked)}
                        label={item.name}
                        checked={selectedItems.some((selectedItem) => selectedItem.key === itemKey)}
                      />
                    </div>
                    <div className='ml-4 flex-shrink-0'>
                      <Text size={textSize} className='font-medium'>
                        {itemQuantity > 1 ? (
                          <>
                            <Money amount={itemPrice} currency={item.price?.currency} /> {`× ${itemQuantity} = `}
                            <Money amount={section75LineTotal(itemPrice, itemQuantity)} currency={item.price?.currency} />
                          </>
                        ) : (
                          <Money amount={itemPrice} currency={item.price?.currency} />
                        )}
                      </Text>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className='mt-6'>
              <Button padding='Fluid' size='Large' onClick={handleNext} disabled={isNextDisabled}>
                {t('dispute:section75DisputeModal:chooseItems:next')}
              </Button>
            </div>
          </>
        )}
      </Modal.Content>
    </>
  )
}
